// Tournament system configuration

import type { PadelTeam, MatchResult } from '@/game/systems/MatchSimulator'
import { Rng, defaultRng, rollInt } from '@/game/utils/rng'
import { generatePairName } from './playersConfig'

export interface BracketMatch {
  id: string
  round: number // 0 = first round
  slot: number // position within the round
  teamA: PadelTeam | null
  teamB: PadelTeam | null
  result: MatchResult | null
}

export interface Bracket {
  matches: BracketMatch[]
  totalRounds: number
  currentRound: number // next round to resolve
}

export interface Tournament {
  id: string
  name: string
  type: TournamentType
  entryFee: number
  prizePool: number
  participants: number
  maxParticipants: number
  startDay: number
  duration: number // days
  status: TournamentStatus
  requirements: TournamentRequirement[]
  bracket?: Bracket | null
  clubPairIds?: string[] | null // ids of the club players entered as a pair
}

export type TournamentType = 'amateur' | 'intermediate' | 'professional' | 'championship'
export type TournamentStatus = 'upcoming' | 'in_progress' | 'completed'

export interface TournamentRequirement {
  type: 'min_courts' | 'min_reputation' | 'has_building' | 'min_division'
  value: number | string
}

export interface TournamentDefinition {
  type: TournamentType
  name: string
  emoji: string
  description: string
  entryFeeRange: [number, number]
  prizeMultiplier: number // multiplier of total entry fees
  participantsRange: [number, number]
  duration: number
  requirements: TournamentRequirement[]
  reputationBonus: number
}

export const TOURNAMENT_DEFINITIONS: Record<TournamentType, TournamentDefinition> = {
  amateur: {
    type: 'amateur',
    name: 'Torneo Amateur',
    emoji: '🎾',
    description: 'Competición local para jugadores principiantes',
    entryFeeRange: [20, 50],
    prizeMultiplier: 0.8,
    participantsRange: [8, 16],
    duration: 1,
    requirements: [
      { type: 'min_courts', value: 1 },
    ],
    reputationBonus: 0.1,
  },
  intermediate: {
    type: 'intermediate',
    name: 'Torneo Intermedio',
    emoji: '🏅',
    description: 'Competición regional para jugadores con experiencia',
    entryFeeRange: [50, 100],
    prizeMultiplier: 0.85,
    participantsRange: [16, 32],
    duration: 2,
    requirements: [
      { type: 'min_courts', value: 2 },
      { type: 'min_reputation', value: 3 },
    ],
    reputationBonus: 0.2,
  },
  professional: {
    type: 'professional',
    name: 'Torneo Profesional',
    emoji: '🏆',
    description: 'Competición de alto nivel con jugadores profesionales',
    entryFeeRange: [150, 300],
    prizeMultiplier: 0.9,
    participantsRange: [32, 64],
    duration: 3,
    requirements: [
      { type: 'min_courts', value: 4 },
      { type: 'min_reputation', value: 4 },
      { type: 'has_building', value: 'locker' },
    ],
    reputationBonus: 0.4,
  },
  championship: {
    type: 'championship',
    name: 'Campeonato Nacional',
    emoji: '👑',
    description: 'El evento más prestigioso del país',
    entryFeeRange: [500, 1000],
    prizeMultiplier: 0.95,
    participantsRange: [64, 128],
    duration: 5,
    requirements: [
      { type: 'min_courts', value: 6 },
      { type: 'min_reputation', value: 4.5 },
      { type: 'has_building', value: 'locker' },
      { type: 'has_building', value: 'cafe' },
      // Hosting the national championship needs the Licencia Nacional
      { type: 'min_division', value: 2 },
    ],
    reputationBonus: 1.0,
  },
}

// Generate a random tournament
export function generateTournament(type: TournamentType, startDay: number): Tournament {
  const def = TOURNAMENT_DEFINITIONS[type]
  const participants = Math.floor(
    Math.random() * (def.participantsRange[1] - def.participantsRange[0]) + def.participantsRange[0]
  )
  const entryFee = Math.floor(
    Math.random() * (def.entryFeeRange[1] - def.entryFeeRange[0]) + def.entryFeeRange[0]
  )
  
  return {
    id: crypto.randomUUID(),
    name: `${def.name} ${startDay}`,
    type,
    entryFee,
    prizePool: Math.floor(participants * entryFee * def.prizeMultiplier),
    participants: 0,
    maxParticipants: participants,
    startDay,
    duration: def.duration,
    status: 'upcoming',
    requirements: def.requirements,
  }
}

// Check if player meets tournament requirements. `currentDivision` uses the
// league scale (1 best .. 4, or the "sin liga" sentinel 5); omit to skip
// division gating (legacy callers).
export function canHostTournament(
  def: TournamentDefinition,
  courtsCount: number,
  reputation: number,
  hasBuilding: (type: string) => boolean,
  currentDivision?: number,
): { canHost: boolean; missingReqs: string[] } {
  const missingReqs: string[] = []

  for (const req of def.requirements) {
    switch (req.type) {
      case 'min_courts':
        if (courtsCount < (req.value as number)) {
          missingReqs.push(`Mínimo ${req.value} pistas`)
        }
        break
      case 'min_reputation':
        if (reputation < (req.value as number)) {
          missingReqs.push(`Reputación mínima ${req.value}⭐`)
        }
        break
      case 'has_building':
        if (!hasBuilding(req.value as string)) {
          missingReqs.push(`Requiere ${req.value}`)
        }
        break
      case 'min_division':
        if (currentDivision !== undefined && currentDivision > (req.value as number)) {
          missingReqs.push(`Requiere ${req.value}ª División (Licencia Federativa)`)
        }
        break
    }
  }

  return { canHost: missingReqs.length === 0, missingReqs }
}

// Calculate tournament income for the club
export function calculateTournamentIncome(tournament: Tournament): number {
  // Club gets % of entry fees + concessions
  const entryIncome = tournament.participants * tournament.entryFee * 0.2
  const concessionsIncome = tournament.participants * 10 // Per participant
  return Math.floor(entryIncome + concessionsIncome)
}

// === Brackets ===

/** NPC pair strength by tournament tier */
export const NPC_RATING_RANGES: Record<TournamentType, [number, number]> = {
  amateur: [25, 45],
  intermediate: [40, 60],
  professional: [55, 80],
  championship: [70, 95],
}

export function generateNpcTeam(type: TournamentType, rng: Rng = defaultRng): PadelTeam {
  const [min, max] = NPC_RATING_RANGES[type]
  return {
    name: generatePairName(rng),
    rating: rollInt(min, max, rng),
    form: rollInt(35, 65, rng),
    isClubTeam: false,
  }
}

/** Bracket size in pairs: nearest power of two <= participants/2, clamped [4, 32] */
export function bracketTeamCount(participants: number): number {
  const pairs = Math.floor(participants / 2)
  let size = 4
  while (size * 2 <= pairs && size * 2 <= 32) size *= 2
  return size
}

/** Rounds resolved per in-progress day so the bracket fits the duration */
export function roundsPerDay(totalRounds: number, duration: number): number {
  return Math.max(1, Math.ceil(totalRounds / Math.max(1, duration)))
}

/**
 * Build a single-elimination bracket. teams.length must be a power of two.
 * First-round matches are seeded in array order; later rounds start empty.
 */
export function generateBracket(teams: PadelTeam[]): Bracket {
  const totalRounds = Math.round(Math.log2(teams.length))
  const matches: BracketMatch[] = []

  for (let round = 0; round < totalRounds; round++) {
    const matchesInRound = teams.length / Math.pow(2, round + 1)
    for (let slot = 0; slot < matchesInRound; slot++) {
      matches.push({
        id: `r${round}-m${slot}`,
        round,
        slot,
        teamA: round === 0 ? teams[slot * 2] : null,
        teamB: round === 0 ? teams[slot * 2 + 1] : null,
        result: null,
      })
    }
  }

  return { matches, totalRounds, currentRound: 0 }
}

/** Winner of the whole bracket (null until the final is resolved) */
export function getBracketChampion(bracket: Bracket): PadelTeam | null {
  const final = bracket.matches.find(m => m.round === bracket.totalRounds - 1)
  if (!final?.result || !final.teamA || !final.teamB) return null
  return final.result.winner === 0 ? final.teamA : final.teamB
}
